import type { JSX } from "react";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Category } from "../api.js";
import { Modal } from "../components/Modal.js";
import { Field, Select, TextInput } from "../components/Form.js";
import { PageHeader } from "../components/PageHeader.js";
import { Button } from "../components/ui/button.js";
import { Card } from "../components/ui/card.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table.js";

export function Categories(): JSX.Element {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Category | null>(null);
  const [showForm, setShowForm] = useState(false);
  const q = useQuery({ queryKey: ["categories"], queryFn: () => api.list<Category>("/categories") });
  const remove = useMutation({
    mutationFn: (id: string) => api.remove(`/categories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["categories"] }),
  });

  return (
    <>
      <PageHeader title="Categories">
        <Button
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
        >
          + Add category
        </Button>
      </PageHeader>

      <Card className="overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Archived</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.data?.items.map((c) => (
              <TableRow key={c.id}>
                <TableCell>{c.type}</TableCell>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="text-muted-foreground">{c.slug}</TableCell>
                <TableCell>{c.is_archived ? "yes" : "—"}</TableCell>
                <TableCell>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditing(c);
                        setShowForm(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Delete ${c.name}?`)) remove.mutate(c.id);
                      }}
                    >
                      Del
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Modal
        open={showForm}
        onClose={() => {
          setShowForm(false);
        }}
        title={editing ? `Edit ${editing.name}` : "New category"}
      >
        <CategoryForm
          initial={editing}
          onDone={() => {
            setShowForm(false);
            void qc.invalidateQueries({ queryKey: ["categories"] });
          }}
        />
      </Modal>
    </>
  );
}

function CategoryForm({
  initial,
  onDone,
}: {
  initial: Category | null;
  onDone: () => void;
}): JSX.Element {
  const [type, setType] = useState(initial?.type ?? "expense");
  const [name, setName] = useState(initial?.name ?? "");
  const [slug, setSlug] = useState(initial?.slug ?? "");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        type,
        name,
        slug: slug || name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""),
      };
      return initial
        ? api.update(`/categories/${initial.id}`, body)
        : api.create("/categories", body);
    },
    onSuccess: onDone,
    onError: (e: Error) => {
      setError(e.message);
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Field label="Type">
        <Select
          value={type}
          onChange={(e) => {
            setType(e.target.value as "expense" | "income");
          }}
        >
          <option value="expense">expense</option>
          <option value="income">income</option>
        </Select>
      </Field>
      <Field label="Name *">
        <TextInput
          required
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
          placeholder="Coffee shops"
        />
      </Field>
      <Field label="Slug (auto if empty)">
        <TextInput
          value={slug}
          onChange={(e) => {
            setSlug(e.target.value);
          }}
          placeholder="coffee-shops"
        />
      </Field>
      {error && <p className="text-bad mb-3 text-sm">{error}</p>}
      <Button type="submit" disabled={save.isPending}>
        {save.isPending ? "Saving..." : "Save"}
      </Button>
    </form>
  );
}
